import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, TimerOff, LayoutDashboard } from "lucide-react";

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export default function SectionTransitionPage() {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [left, setLeft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const expiredRef = useRef(false);

  useEffect(() => {
    const sid = localStorage.getItem("its_test_session");
    if (!sid) { navigate("/dashboard"); return; }
    api.get(`/tests/${sid}`).then(({ data }) => {
      if (data.status === "section_a_active") { navigate("/test"); return; }
      if (["completed", "screened_out", "terminated"].includes(data.status)) { navigate("/test/completamento"); return; }
      if (data.section_b_started_at) { navigate("/test/sezione-b"); return; }
      setSession(data);
    }).catch(() => navigate("/dashboard"));
  }, [navigate]);

  const proceed = async () => {
    setBusy(true);
    try {
      await api.post(`/tests/${session.id}/start-section-b`);
      navigate("/test/sezione-b");
    } catch (e) {
      setBusy(false);
      if (e?.response?.status === 400) {
        setTimedOut(true);
      } else {
        toast.error(formatApiError(e));
      }
    }
  };

  // Allo scadere dei 5 minuti: il backend registra la candidatura come non completata
  useEffect(() => {
    if (!session?.section_b_start_deadline || timedOut) return;
    const tick = () => {
      const l = Math.max(0, Math.floor((new Date(session.section_b_start_deadline) - Date.now()) / 1000));
      setLeft(l);
      if (l <= 0 && !expiredRef.current) {
        expiredRef.current = true;
        proceed();
      }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, timedOut]);

  if (!session) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center" data-testid="transition-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-700 border-t-sky-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 flex items-center justify-center px-4" data-testid="transition-page">
      <div className="max-w-lg w-full text-center py-16">
        {timedOut ? (
          <>
            <TimerOff className="h-14 w-14 text-amber-400 mx-auto mb-4" />
            <h1 className="font-display text-2xl sm:text-3xl font-bold">Tempo scaduto</h1>
            <p className="mt-3 text-sm text-slate-300 leading-relaxed" data-testid="transition-timeout-message">
              Non hai avviato la Parte B entro 5 minuti dal completamento della Parte A: la tua candidatura è
              stata registrata come non completata. Per informazioni contatta la segreteria dell'istituto.
            </p>
            <Button data-testid="transition-dashboard-button" className="mt-8 bg-sky-500 hover:bg-sky-400 text-slate-900"
              onClick={() => navigate("/dashboard")}>
              <LayoutDashboard className="h-4 w-4 mr-2" /> Torna all'area riservata
            </Button>
          </>
        ) : (
          <>
            <CheckCircle2 className="h-14 w-14 text-green-400 mx-auto mb-4" />
            <p className="proctor-badge text-sky-400 mb-2">PARTE A COMPLETATA</p>
            <h1 className="font-display text-2xl sm:text-3xl font-bold">Hai completato la Parte A</h1>
            <p className="mt-3 text-sm text-slate-300 leading-relaxed">
              La selezione prosegue con la <strong>Parte B</strong>: la rotta ITS, la mappa di allineamento al percorso
              {session.duration_b_minutes ? ` (circa ${session.duration_b_minutes} minuti)` : ""}, senza risposte
              giuste o sbagliate. Rispondi in modo istintivo e sincero.
            </p>
            {left !== null && (
              <div className="mt-6 bg-slate-800/80 border border-slate-700 rounded-xl p-4">
                <p className="text-xs text-slate-400 uppercase tracking-widest">Tempo per iniziare la Parte B</p>
                <p data-testid="transition-countdown" className={`mt-1 font-mono text-3xl font-bold ${left <= 60 ? "text-red-400" : "text-slate-100"}`}>
                  {fmt(left)}
                </p>
              </div>
            )}
            <Button data-testid="transition-proceed-button" size="lg" disabled={busy}
              className="mt-8 bg-sky-500 hover:bg-sky-400 text-slate-900 font-semibold"
              onClick={proceed}>
              {busy ? "Attendere..." : <>Prosegui con la Parte B <ArrowRight className="h-4 w-4 ml-2" /></>}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
